import API from '@js/api'

const types = {
  SET_SKIN: 'SET_SKIN',
  SET_TROOP: 'SET_TROOP',
  SET_HERO_WORE: 'SET_HERO_WORE',
  SET_EXP: 'SET_EXP',
  SET_LEVEL: 'SET_LEVEL',
  SET_COMMAND_VALUE: 'SET_COMMAND_VALUE',
  SET_PRIMARY_SKILLS: 'SET_PRIMARY_SKILLS',
  SET_ADDITION_PRIMARY_SKILLS: 'SET_ADDITION_PRIMARY_SKILLS',
  SET_SECONDARY_SKILLS: 'SET_SECONDARY_SKILLS',
  SET_UNUSED_PRIMARY_SKILL_POINT: 'SET_UNUSED_PRIMARY_SKILL_POINT',
  SET_MAX_SECONDARY_SKILL_POINT: 'SET_MAX_SECONDARY_SKILL_POINT',
  SET_GOLD: 'SET_GOLD',
  SET_RESOURCE: 'SET_RESOURCE',
  SET_BACKPACK_ITEMS: 'SET_BACKPACK_ITEMS',
}

const state = {
  skin: null,
  troop: [{}, {}, {}, {}, {}, {}, {}],
  heroWore: [],
  level: 1,
  exp: 0,
  commandValue: 0,
  primarySkills: {
    attack: 0,
    defense: 0,
    power: 0,
    knowledge: 0,
  },
  additionPrimarySkills: {
    attack: 0,
    defense: 0,
    power: 0,
    knowledge: 0,
  },
  secondarySkills: {},
  morale: 0,
  luck: 0,
  unusedPrimarySkillPoint: 0,
  maxSecondarySkillPoint: 0,
  gold: 0,
  wood: 0,
  ore: 0,
  crystal: 0,
  gem: 0,
  mercury: 0,
  sulfur: 0,
  moshi: 0,
  maxActionPoint: 0,
  actionPoint: 0,
  autoCombatCount: 0,
  backpackItems: [],
  necromancy: {},
  sceneId: null,
  sceneCoords: '',
  reputation: [],
}

const getters = {
  troop (state) {
    return state.troop
  },
  heroWore (state) {
    return state.heroWore
  },
  hasTroop (state) {
    if (!state.troop || !state.troop.length) {
      return false
    }
    return state.troop.some((t) => {
      const key = Object.keys(t)[0]
      return !!t[key]
    })
  },
  level (state) {
    return state.level
  },
  exp (state) {
    return state.exp
  },
  commandValue (state) {
    let equipmentCommandPercent = 0
    state.heroWore.forEach((item) => {
      item.effect?.forEach((effect) => {
        if (effect.attr === 'command') {
          if (effect.isPercent) {
            equipmentCommandPercent += effect.value
          }
        }
      })
    })
    return ~~(state.commandValue * (1 + equipmentCommandPercent))
  },
  primarySkills (state) {
    return state.primarySkills
  },
  additionPrimarySkills (state) {
    return state.additionPrimarySkills
  },
  secondarySkills (state) {
    return state.secondarySkills || {}
  },
  morale (state) {
    return +state.morale || 0
  },
  luck (state) {
    return +state.luck || 0
  },
  unusedPrimarySkillPoint (state) {
    return state.unusedPrimarySkillPoint
  },
  maxSecondarySkillPoint (state) {
    return state.maxSecondarySkillPoint
  },
  gold (state) {
    return state.gold
  },
  wood (state) {
    return state.wood
  },
  ore (state) {
    return state.ore
  },
  crystal (state) {
    return state.crystal
  },
  gem (state) {
    return state.gem
  },
  mercury (state) {
    return state.mercury
  },
  sulfur (state) {
    return state.sulfur
  },
  moshi (state) {
    return state.moshi
  },
  backpackItems (state) {
    return state.backpackItems
  },
  maxActionPoint (state) {
    return state.maxActionPoint || 0
  },
  actionPoint (state) {
    return state.actionPoint || 0
  },
}

const actions = {
  async getHeroInfo ({ commit }) {
    try {
      let {
        troop, exp, level, commandValue, skin,
        attack, defense, power, knowledge,
        attackAddition, defenseAddition, powerAddition, knowledgeAddition,
        unusedPrimarySkillPoint, secondarySkills, maxSecondarySkillPoint,
        gold, wood, ore, crystal, gem, mercury, sulfur, moshi,
        maxActionPoint, actionPoint, autoCombatCount,
        necromancy,
        sceneId, sceneCoords
      } = await API.hero.getHeroInfo()
      troop = JSON.parse(troop).map(d => {
        const name = Object.keys(d)[0]
        const amount = d[name]
        return {
          name: name,
          amount: amount,
        }
      })
      commit(types.SET_SKIN, skin)
      commit(types.SET_TROOP, troop)
      commit(types.SET_EXP, exp)
      commit(types.SET_LEVEL, level)
      commit(types.SET_COMMAND_VALUE, commandValue)
      commit(types.SET_PRIMARY_SKILLS, { attack, defense, power, knowledge })
      commit(types.SET_ADDITION_PRIMARY_SKILLS, { attackAddition, defenseAddition, powerAddition, knowledgeAddition })
      commit(types.SET_UNUSED_PRIMARY_SKILL_POINT, unusedPrimarySkillPoint)
      commit(types.SET_SECONDARY_SKILLS, secondarySkills)
      commit(types.SET_MAX_SECONDARY_SKILL_POINT, maxSecondarySkillPoint)
      commit(types.SET_GOLD, gold)
      commit(types.SET_RESOURCE, { wood })
      commit(types.SET_RESOURCE, { ore })
      commit(types.SET_RESOURCE, { crystal })
      commit(types.SET_RESOURCE, { gem })
      commit(types.SET_RESOURCE, { mercury })
      commit(types.SET_RESOURCE, { sulfur })
      commit(types.SET_RESOURCE, { moshi })
      commit('setMaxActionPoint', maxActionPoint)
      commit('setActionPoint', actionPoint)
      commit('setAutoCombatCount', autoCombatCount)
      commit('setNecromancy', necromancy)
      commit('setSceneId', sceneId)
      commit('setSceneCoords', sceneCoords)

      const heroWore = await API.hero.getHeroWore()
      commit(types.SET_HERO_WORE, heroWore)
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  },

  async getHeroWore ({ commit }) {
    try {
      const heroWore = await API.hero.getHeroWore()
      commit(types.SET_HERO_WORE, heroWore)
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  },

  async getPrimarySkills ({ commit }) {
    try {
      let { attack, defense, power, knowledge, unusedPrimarySkillPoint } = await API.hero.getPrimarySkills()
      commit(types.SET_PRIMARY_SKILLS, { attack, defense, power, knowledge })
      commit(types.SET_UNUSED_PRIMARY_SKILL_POINT, unusedPrimarySkillPoint)
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  },

  async getSecondarySkills ({ commit }) {
    try {
      let { secondarySkills, maxSecondarySkillPoint } = await API.hero.getSecondarySkills()
      commit(types.SET_SECONDARY_SKILLS, secondarySkills)
      commit(types.SET_MAX_SECONDARY_SKILL_POINT, maxSecondarySkillPoint)
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  },

  async getBackpackItems ({ commit }) {
    try {
      const res = await API.hero.getBackpackItems()
      commit(types.SET_BACKPACK_ITEMS, res)
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  },

  async getGold ({ commit }) {
    try {
      const gold = await API.hero.getGold()
      commit(types.SET_GOLD, gold)
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  },

  async getActionPoint ({ commit }) {
    try {
      const value = await API.hero.getActionPoint()
      commit('setActionPoint', value)
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  },

  getTroop ({ commit }) {
    return new Promise(async (resolve, reject) => {
      try {
        let troop = await API.hero.getTroop()
        troop = JSON.parse(troop).map(d => {
          const name = Object.keys(d)[0]
          const amount = d[name]
          return {
            name: name,
            amount: amount,
          }
        })
        commit(types.SET_TROOP, troop)
        resolve()
      } catch (err) {
        console.warn(err)
        reject(err)
      }
    })
  },

  updateTroop ({ commit }, troop) {
    return new Promise(async (resolve, reject) => {
      try {
        const _troop = troop.map(d => {
          const { name, amount } = d
          if (!name || !amount) {
            return {}
          }

          return { [name]: amount }
        })
        await API.hero.updateTroop({ troop: _troop })
        commit(types.SET_TROOP, troop)
        resolve()
      } catch (err) {
        console.warn(err)
        reject(err)
      }
    })
  },

  getReputation ({ commit }) {
    return new Promise(async (resolve, reject) => {
      try {
        const ret = await API.hero.getReputation()
        commit('setReputation', ret)
        resolve()
      } catch (err) {
        console.warn(err)
        reject(err)
      }
    })
  },
}

const mutations = {
  reset () {
    state.heroWore = []
    state.backpackItems = []
  },

  [types.SET_TROOP] (state, troop) {
    state.troop = troop
  },
  [types.SET_SKIN] (state, skin) {
    state.skin = skin
  },
  [types.SET_HERO_WORE] (state, heroWore) {
    state.heroWore = heroWore
  },
  [types.SET_EXP] (state, exp) {
    state.exp = exp
  },
  [types.SET_LEVEL] (state, level) {
    state.level = level
  },
  [types.SET_COMMAND_VALUE] (state, commandValue) {
    state.commandValue = commandValue
  },
  [types.SET_PRIMARY_SKILLS] (state, { attack, defense, power, knowledge }) {
    state.primarySkills = { attack, defense, power, knowledge }
  },
  [types.SET_ADDITION_PRIMARY_SKILLS] (state, { attackAddition, defenseAddition, powerAddition, knowledgeAddition }) {
    state.additionPrimarySkills = {
      attack: attackAddition || 0,
      defense: defenseAddition || 0,
      power: powerAddition || 0,
      knowledge: knowledgeAddition || 0,
    }
  },
  [types.SET_SECONDARY_SKILLS] (state, config) {
    state.secondarySkills = config || {}
    state.morale = +state.secondarySkills.Leadership || 0
    state.luck = +state.secondarySkills.Luck || 0
  },
  [types.SET_UNUSED_PRIMARY_SKILL_POINT] (state, point) {
    state.unusedPrimarySkillPoint = Math.max(0, +point || 0)
  },
  [types.SET_MAX_SECONDARY_SKILL_POINT] (state, point) {
    state.maxSecondarySkillPoint = +point || 0
  },
  [types.SET_GOLD] (state, gold) {
    state.gold = +gold || 0
  },
  [types.SET_RESOURCE] (state, data) {
    const key = Object.keys(data)[0]
    const value = data[key]
    if (['wood',
      'ore',
      'crystal',
      'gem',
      'mercury',
      'sulfur',
      'moshi'].includes(key)) {
      state[key] = +value || 0
    }
  },
  [types.SET_BACKPACK_ITEMS] (state, items = []) {
    state.backpackItems = items
  },
  setMaxActionPoint (state, payload) {
    state.maxActionPoint = +payload || 0
  },
  setActionPoint (state, payload) {
    state.actionPoint = +payload || 0
  },
  setAutoCombatCount (state, payload) {
    state.autoCombatCount = +payload || 0
  },
  setNecromancy (state, payload) {
    let config = null
    if (payload) {
      try {
        config = JSON.parse(payload)
      } catch (err) {
      }
    }
    state.necromancy = config
  },
  setSceneId (state, payload) {
    state.sceneId = payload
  },
  setSceneCoords (state, payload) {
    state.sceneCoords = payload
  },
  setReputation (state, data) {
    state.reputation = data || []
  },
}

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions
}
